import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { 
  Dumbbell, 
  Utensils, 
  Activity, 
  Film, 
  Brain, 
  ChevronDown, 
  ChevronUp,
  Clock,
  ExternalLink
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';

const CATEGORY_CONFIG: Record<string, { 
  icon: React.ComponentType<{ className?: string }>; 
  label: string; 
  emoji: string;
  color: string;
}> = {
  workout: { icon: Dumbbell, label: 'Workout', emoji: '💪', color: 'bg-orange-500/10 text-orange-600 border-orange-500/20' },
  nutrition: { icon: Utensils, label: 'Nutrition', emoji: '🥗', color: 'bg-green-500/10 text-green-600 border-green-500/20' },
  recovery: { icon: Activity, label: 'Recovery', emoji: '🧘', color: 'bg-blue-500/10 text-blue-600 border-blue-500/20' },
  film_study: { icon: Film, label: 'Film Study', emoji: '📽️', color: 'bg-purple-500/10 text-purple-600 border-purple-500/20' },
  mental: { icon: Brain, label: 'Mental', emoji: '🧠', color: 'bg-pink-500/10 text-pink-600 border-pink-500/20' },
};

interface TrainingTemplateData {
  training_type: 'checkin' | 'routine';
  training_id: string;
  category: string;
  title: string;
  notes?: string;
  duration_minutes?: number;
  description?: string;
  routine_data?: {
    exercises?: Array<{ name: string; sets: number; reps: number; notes?: string }>;
    items?: Array<{ name: string; amount?: string; notes?: string }>;
    meal_type?: string;
  };
}

interface TrainingWallPostCardProps {
  templateData: TrainingTemplateData;
  profileId: string;
  profileUsername?: string;
}

const TrainingWallPostCard: React.FC<TrainingWallPostCardProps> = ({
  templateData,
  profileId,
  profileUsername,
}) => {
  const { user } = useAuth();
  const [isExpanded, setIsExpanded] = useState(false);
  
  const config = CATEGORY_CONFIG[templateData.category] || CATEGORY_CONFIG.workout;
  const Icon = config.icon;
  const isCheckin = templateData.training_type === 'checkin';
  const isRoutine = templateData.training_type === 'routine';

  // Generate link based on login status
  const getViewLink = () => {
    if (user) {
      // Logged in: go to training feed (could filter by content)
      return `/training?highlight=${templateData.training_id}`;
    } else {
      // Not logged in: go to athlete profile training section
      return `/profile/${profileUsername || profileId}#training`;
    }
  };

  return (
    <Card className="bg-gradient-to-br from-primary/5 to-transparent border-primary/20">
      <CardContent className="pt-4 space-y-3">
        {/* Training Badge */}
        <div className="flex items-center gap-2">
          <Badge variant="outline" className={cn("gap-1.5", config.color)}>
            <Icon className="h-3 w-3" />
            {isCheckin ? 'Check-in' : 'Routine'}: {config.label}
          </Badge>
          {templateData.duration_minutes && (
            <Badge variant="secondary" className="gap-1">
              <Clock className="h-3 w-3" />
              {templateData.duration_minutes} min
            </Badge>
          )}
        </div>

        {/* Title */}
        <h4 className="font-semibold text-lg">
          {config.emoji} {templateData.title}
        </h4>

        {/* Check-in Notes */}
        {isCheckin && templateData.notes && (
          <p className="text-muted-foreground text-sm whitespace-pre-wrap">
            {templateData.notes}
          </p>
        )}

        {/* Routine Description */}
        {isRoutine && templateData.description && (
          <p className="text-muted-foreground text-sm">
            {templateData.description}
          </p>
        )}

        {/* Routine Details (Collapsible) */}
        {isRoutine && templateData.routine_data && (
          <Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
            <CollapsibleTrigger asChild>
              <Button variant="ghost" size="sm" className="gap-2 w-full justify-between">
                <span className="text-sm font-medium">
                  {templateData.routine_data.exercises 
                    ? `${templateData.routine_data.exercises.length} exercises`
                    : templateData.routine_data.items 
                      ? `${templateData.routine_data.items.length} items`
                      : 'View Details'}
                </span>
                {isExpanded ? (
                  <ChevronUp className="h-4 w-4" />
                ) : (
                  <ChevronDown className="h-4 w-4" />
                )}
              </Button>
            </CollapsibleTrigger>
            
            <CollapsibleContent className="mt-2">
              {/* Workout Exercises */}
              {templateData.routine_data.exercises && (
                <div className="space-y-2 bg-muted/50 rounded-lg p-3">
                  {templateData.routine_data.exercises.map((exercise, idx) => (
                    <div key={idx} className="flex items-center justify-between text-sm">
                      <span className="font-medium">{exercise.name}</span>
                      <span className="text-muted-foreground">
                        {exercise.sets} × {exercise.reps}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {/* Nutrition Items */}
              {templateData.routine_data.items && (
                <div className="space-y-2 bg-muted/50 rounded-lg p-3">
                  {templateData.routine_data.meal_type && (
                    <p className="text-xs text-muted-foreground uppercase tracking-wide mb-2">
                      {templateData.routine_data.meal_type}
                    </p>
                  )}
                  {templateData.routine_data.items.map((item, idx) => (
                    <div key={idx} className="flex items-center justify-between text-sm">
                      <span className="font-medium">{item.name}</span>
                      {item.amount && (
                        <span className="text-muted-foreground">{item.amount}</span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CollapsibleContent>
          </Collapsible>
        )}

        {/* View Link */}
        <Link 
          to={getViewLink()}
          className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline"
        >
          <ExternalLink className="h-3 w-3" />
          {isCheckin ? 'View Check-in' : 'View Full Routine'}
        </Link>
      </CardContent>
    </Card>
  );
};

export default TrainingWallPostCard;
